import React, { useState, useRef } from 'react';
import { Upload, Link as LinkIcon, X, Image as ImageIcon, Loader, AlertCircle, CheckCircle } from 'lucide-react';
import { uploadImage } from '../utils/supabase';
import { compressImage, validateImageType, validateImageSize, processDropboxUrl, formatFileSize } from '../utils/imageUtils';

const ImageUploadField = ({ value, onChange, dayNumber }) => {
  const [uploadMethod, setUploadMethod] = useState('url'); // 'url' أو 'upload'
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [error, setError] = useState('');
  const [previewUrl, setPreviewUrl] = useState(value || '');
  const fileInputRef = useRef(null);

  // معالجة تغيير طريقة الإدخال
  const handleMethodChange = (method) => {
    setUploadMethod(method);
    setError('');
    if (method === 'upload') {
      setPreviewUrl('');
      onChange('');
    }
  };

  // معالجة إدخال اللينك
  const handleUrlChange = (e) => {
    const url = e.target.value;
    const processedUrl = processDropboxUrl(url);
    setPreviewUrl(processedUrl);
    onChange(processedUrl);
    setError('');
  };

  // معالجة اختيار الملف
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError('');
    setIsUploading(true);
    setUploadProgress('جاري التحقق من الملف...');

    try {
      // التحقق من نوع الملف
      if (!validateImageType(file)) {
        throw new Error('نوع الملف غير مدعوم. يرجى اختيار صورة (JPG, PNG, GIF, WebP)');
      }

      // التحقق من حجم الملف (قبل الضغط)
      if (!validateImageSize(file, 10)) {
        throw new Error('حجم الملف كبير جداً. الحد الأقصى 10 ميجابايت');
      }

      const originalSize = formatFileSize(file.size);
      setUploadProgress(`الحجم الأصلي: ${originalSize} - جاري ضغط الصورة...`);

      // ضغط الصورة
      const compressedFile = await compressImage(file, {
        maxWidth: 1920,
        maxHeight: 1920,
        quality: 0.8,
        mimeType: 'image/jpeg'
      });

      const compressedSize = formatFileSize(compressedFile.size);
      setUploadProgress(`تم الضغط: ${compressedSize} - جاري الرفع...`);

      // رفع الصورة إلى Supabase
      const { url, error: uploadError } = await uploadImage(compressedFile, 'ramadan-gifts');

      if (uploadError) {
        throw new Error(uploadError);
      }

      // حفظ الرابط
      setPreviewUrl(url);
      onChange(url);
      setUploadProgress('تم الرفع بنجاح! ✅');
      
      // مسح رسالة النجاح بعد 3 ثوانٍ
      setTimeout(() => {
        setUploadProgress('');
      }, 3000);

    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'حدث خطأ أثناء رفع الصورة');
      setPreviewUrl('');
      onChange('');
    } finally {
      setIsUploading(false);
      // مسح اختيار الملف
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // مسح الصورة
  const handleClear = () => {
    setPreviewUrl('');
    onChange('');
    setError('');
    setUploadProgress('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-4">
      {/* اختيار طريقة الإدخال */}
      <div className="flex gap-2 bg-white/5 p-1 rounded-lg">
        <button
          type="button"
          onClick={() => handleMethodChange('url')}
          className={`flex-1 px-4 py-2 rounded-md text-sm font-bold transition-all flex items-center justify-center gap-2 ${
            uploadMethod === 'url'
              ? 'bg-yellow-400 text-indigo-950'
              : 'text-white hover:bg-white/10'
          }`}
          disabled={isUploading}
        >
          <LinkIcon className="w-4 h-4" />
          <span>لينك الصورة</span>
        </button>
        <button
          type="button"
          onClick={() => handleMethodChange('upload')}
          className={`flex-1 px-4 py-2 rounded-md text-sm font-bold transition-all flex items-center justify-center gap-2 ${
            uploadMethod === 'upload'
              ? 'bg-yellow-400 text-indigo-950'
              : 'text-white hover:bg-white/10'
          }`}
          disabled={isUploading}
        >
          <Upload className="w-4 h-4" />
          <span>رفع صورة</span>
        </button>
      </div>

      {/* حقل إدخال اللينك */}
      {uploadMethod === 'url' && (
        <div>
          <input
            type="url"
            value={previewUrl}
            onChange={handleUrlChange}
            placeholder="https://example.com/image.jpg أو رابط Dropbox"
            className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-white/50 focus:outline-none focus:border-yellow-400 text-left"
            disabled={isUploading}
          />
          <p className="text-xs text-purple-300 mt-2">
            💡 يمكنك لصق رابط من Imgur, Dropbox, أو أي موقع آخر
          </p>
        </div>
      )}

      {/* حقل رفع الملف */}
      {uploadMethod === 'upload' && (
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
            id={`file-upload-${dayNumber}`}
            disabled={isUploading}
          />
          <label
            htmlFor={`file-upload-${dayNumber}`}
            className={`w-full px-4 py-8 rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-3 transition-all cursor-pointer ${
              isUploading
                ? 'border-yellow-400/50 bg-yellow-400/10 cursor-not-allowed'
                : 'border-white/30 hover:border-yellow-400 hover:bg-white/5'
            }`}
          >
            {isUploading ? (
              <>
                <Loader className="w-8 h-8 text-yellow-400 animate-spin" />
                <span className="text-sm text-yellow-300 text-center">{uploadProgress}</span>
              </>
            ) : (
              <>
                <Upload className="w-8 h-8 text-purple-300" />
                <span className="text-sm text-white font-bold">اضغط لاختيار صورة</span>
                <span className="text-xs text-purple-300">JPG, PNG, GIF, WebP - حتى 10MB</span>
              </>
            )}
          </label>
        </div>
      )}

      {/* رسالة الخطأ */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-300">{error}</p>
        </div>
      )}

      {/* رسالة النجاح */}
      {uploadProgress && !isUploading && !error && (
        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 flex items-start gap-2">
          <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-green-300">{uploadProgress}</p>
        </div>
      )}

      {/* معاينة الصورة */}
      {previewUrl && !isUploading && (
        <div className="relative rounded-lg overflow-hidden bg-black/20 border border-white/10">
          <img
            src={previewUrl}
            alt={`Preview day ${dayNumber}`}
            className="w-full h-48 object-cover"
            onError={(e) => {
              setError('فشل تحميل الصورة. تأكد من صحة الرابط');
              e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23333" width="100" height="100"/%3E%3Ctext fill="%23666" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3E✕%3C/text%3E%3C/svg%3E';
            }}
          />
          <button
            type="button"
            onClick={handleClear}
            className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-2 rounded-full transition-all"
            title="حذف الصورة"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="absolute bottom-2 left-2 bg-black/60 backdrop-blur-sm px-2 py-1 rounded flex items-center gap-1">
            <ImageIcon className="w-3 h-3 text-green-400" />
            <span className="text-xs text-white">تم التحميل</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUploadField;
